'use strict';
/**
 * Config-Provenance — pure (no Pict / no DOM) helpers for the cascading-configuration viewer.
 *
 * The Configuration service stores config as flat ConfigEntry rows: ONE ROW PER LEAF, each carrying
 * its own LevelContext (scope) + Address. The server's /Configuration/Get cascades + merges across
 * scopes but returns ONLY the effective object — no per-leaf provenance. These helpers reconstruct,
 * for every leaf of the merged object, WHICH SCOPE set it, by matching the merged object's leaves to
 * the raw rows' Addresses.
 *
 * Address grammar (authoritative — mirrors api/config/.../Configuration-{Get,Put}-ByNamespace.js):
 *   - leading '/', object keys joined by '/', a literal '/' inside a key escaped as '\/'.
 *   - ARRAYS AND PRIMITIVES ARE ATOMIC LEAVES. The writer (_getAllLeafAddresses / _flattenConfig)
 *     never recurses into arrays, so Addresses NEVER contain array indices — an array's elements all
 *     share the array's single Address (and therefore its single scope).
 *
 * Object-editor path grammar (mirrors pict-section-objecteditor _walkObject / _walkArray):
 *   - object keys joined by '.', array elements suffixed '[n]'. Because config leaves are atomic, a
 *     config leaf's editor path is a pure dot-path; any '[n]' in a node path means "at/inside an
 *     atomic array", so it inherits that array's provenance (reduce by truncating at the first '[').
 */

// Cascade order — lowest (most general) to highest (most specific). A higher scope wins the merge.
const SCOPE_ORDER = [ 'Global', 'Customer', 'Contract', 'Project', 'User' ];
const MISSING_VALUE = '__MISSING__';
const SLASH_PLACEHOLDER = ' '; // private sentinel standing in for an escaped '\/' during split

/**
 * Rank of a scope in the cascade (higher = more specific = wins). Unknown scopes rank -1.
 * @param {string} pScope
 * @return {number}
 */
function scopeRank(pScope)
{
	return SCOPE_ORDER.indexOf(pScope);
}

/**
 * Translate a ConfigEntry Address (slash form) into an object-editor dot-path.
 * @param {string} pAddress e.g. '/Foo/Bar' or '/Weird\/Key/Leaf'
 * @return {string} e.g. 'Foo.Bar' or 'Weird/Key.Leaf'
 */
function addressToEditorPath(pAddress)
{
	if (!pAddress)
	{
		return '';
	}
	// Protect escaped slashes, split on the real separators, then restore the literal slashes.
	return String(pAddress).replace(/\\\//g, SLASH_PLACEHOLDER)
		.split('/')
		.filter((pSegment) => pSegment.length > 0)
		.map((pSegment) => pSegment.split(SLASH_PLACEHOLDER).join('/'))
		.join('.');
}

/**
 * Translate an object-editor dot-path (object keys only — a config leaf) into a ConfigEntry Address.
 * Inverse of addressToEditorPath for atomic-leaf paths. Bracketed (array-element) node paths must be
 * reduced via editorPathToLeafPath first.
 * @param {string} pPath e.g. 'Foo.Bar'
 * @return {string} e.g. '/Foo/Bar'
 */
function editorPathToAddress(pPath)
{
	if (!pPath)
	{
		return '';
	}
	return String(pPath).split('.')
		.filter((pSegment) => pSegment.length > 0)
		.map((pSegment) => '/' + pSegment.replace(/\//g, '\\/'))
		.join('');
}

/**
 * Reduce an object-editor node path to the path of the config LEAF that owns it. Config arrays are
 * atomic, so anything at or inside an array (a '[n]' segment) collapses to the array's own path.
 * @param {string} pPath e.g. 'Foo.Bar[2].Baz' -> 'Foo.Bar'; 'Foo.Baz' -> 'Foo.Baz'
 * @return {string}
 */
function editorPathToLeafPath(pPath)
{
	if (!pPath)
	{
		return '';
	}
	const tmpBracket = String(pPath).indexOf('[');
	return (tmpBracket >= 0) ? String(pPath).slice(0, tmpBracket) : String(pPath);
}

/**
 * Is this value an atomic config leaf? Primitives, null, and ARRAYS are leaves; plain objects recurse.
 * @param {*} pValue
 * @return {boolean}
 */
function isLeafValue(pValue)
{
	return (pValue === null) || (typeof pValue !== 'object') || Array.isArray(pValue);
}

/**
 * Enumerate every atomic-leaf path of a merged config object, as object-editor dot-paths.
 * @param {object} pObject the merged config
 * @return {Array<string>}
 */
function enumerateLeafPaths(pObject)
{
	const tmpPaths = [];
	const fWalk = (pValue, pBasePath) =>
	{
		if (isLeafValue(pValue))
		{
			if (pBasePath)
			{
				tmpPaths.push(pBasePath);
			}
			return;
		}
		Object.keys(pValue).forEach((pKey) =>
		{
			const tmpChildPath = pBasePath ? (pBasePath + '.' + pKey) : pKey;
			fWalk(pValue[pKey], tmpChildPath);
		});
	};
	fWalk(pObject || {}, '');
	return tmpPaths;
}

/**
 * Resolve the value at an object-key dot-path (no bracket support — config leaves are atomic).
 * @param {object} pObject
 * @param {string} pLeafPath
 * @return {*} the value, or undefined if any segment is missing
 */
function valueAtLeafPath(pObject, pLeafPath)
{
	if (!pLeafPath)
	{
		return pObject;
	}
	let tmpCurrent = pObject;
	const tmpSegments = String(pLeafPath).split('.');
	for (let i = 0; i < tmpSegments.length; i++)
	{
		if (tmpCurrent === null || typeof tmpCurrent !== 'object')
		{
			return undefined;
		}
		tmpCurrent = tmpCurrent[tmpSegments[i]];
	}
	return tmpCurrent;
}

/**
 * Compare two atomic leaf values (primitive or array) for equality.
 * @param {*} pA
 * @param {*} pB
 * @return {boolean}
 */
function leafValuesEqual(pA, pB)
{
	if (pA === pB)
	{
		return true;
	}
	try
	{
		return JSON.stringify(pA) === JSON.stringify(pB);
	}
	catch (pError)
	{
		return false;
	}
}

/**
 * Build a provenance map { [editorPath]: { Scope, ScopeID } } for a merged config, from the raw
 * ConfigEntry rows. Each leaf is attributed to the HIGHEST-ranked scope that has a (non-__MISSING__)
 * row at its Address; any leaf with no matching row is attributed to Global — the one scope a
 * non-super-user cannot read raw rows for, so "no row found" means "inherited from Global".
 *
 * @param {Array<object>} pRows raw ConfigEntry rows ({ Address, LevelContext, LevelContextID, ConfigValue })
 * @param {object} pMergedConfig the effective object from /Configuration/Get
 * @return {object} { [editorPath]: { Scope, ScopeID } }
 */
function buildProvenanceMapFromRows(pRows, pMergedConfig)
{
	// Index the highest-ranked real (non-hide) row per Address.
	const tmpByAddress = {};
	(pRows || []).forEach((pRow) =>
	{
		if (!pRow || typeof pRow.Address !== 'string' || pRow.ConfigValue === MISSING_VALUE)
		{
			return;
		}
		const tmpExisting = tmpByAddress[pRow.Address];
		if (!tmpExisting || scopeRank(pRow.LevelContext) > scopeRank(tmpExisting.Scope))
		{
			tmpByAddress[pRow.Address] = { Scope: pRow.LevelContext, ScopeID: pRow.LevelContextID };
		}
	});
	// Attribute every merged leaf to a scope (Global by elimination).
	const tmpMap = {};
	enumerateLeafPaths(pMergedConfig).forEach((pLeafPath) =>
	{
		const tmpAddress = editorPathToAddress(pLeafPath);
		tmpMap[pLeafPath] = tmpByAddress[tmpAddress] || { Scope: 'Global', ScopeID: 0 };
	});
	return tmpMap;
}

/**
 * Oracle / fallback: compute provenance by diffing the merged config at each successive scope.
 * pMergesByLevel is ordered general -> specific; each entry's Config is the merge UP TO AND INCLUDING
 * that scope (i.e. what /Configuration/Get returns with LevelContext set to that scope). A leaf is
 * attributed to the LAST level at which its value changed from the prior level (= the highest scope
 * that set the effective value).
 *
 * @param {Array<{Scope:string, ScopeID:(number|string), Config:object}>} pMergesByLevel
 * @return {object} { [editorPath]: { Scope, ScopeID } }
 */
function computeProvenanceByDiff(pMergesByLevel)
{
	const tmpLevels = pMergesByLevel || [];
	const tmpDeepest = tmpLevels.length ? tmpLevels[tmpLevels.length - 1].Config : {};
	const tmpMap = {};
	enumerateLeafPaths(tmpDeepest).forEach((pLeafPath) =>
	{
		let tmpPrior;
		let tmpResolved = null;
		for (let i = 0; i < tmpLevels.length; i++)
		{
			const tmpValue = valueAtLeafPath(tmpLevels[i].Config, pLeafPath);
			if (i === 0)
			{
				if (tmpValue !== undefined)
				{
					tmpResolved = { Scope: tmpLevels[i].Scope, ScopeID: tmpLevels[i].ScopeID };
				}
			}
			else if (!leafValuesEqual(tmpValue, tmpPrior))
			{
				tmpResolved = { Scope: tmpLevels[i].Scope, ScopeID: tmpLevels[i].ScopeID };
			}
			tmpPrior = tmpValue;
		}
		tmpMap[pLeafPath] = tmpResolved || { Scope: 'Global', ScopeID: 0 };
	});
	return tmpMap;
}

/**
 * Look up the provenance for an object-editor node by its Path (handles atomic-array descendants by
 * reducing to the owning leaf path). Returns null when the node is a container (no own leaf row).
 * @param {object} pProvenanceMap
 * @param {string} pNodePath
 * @return {{Scope:string, ScopeID:(number|string)}|null}
 */
function lookupProvenance(pProvenanceMap, pNodePath)
{
	if (!pProvenanceMap)
	{
		return null;
	}
	const tmpLeafPath = editorPathToLeafPath(pNodePath);
	return pProvenanceMap[tmpLeafPath] || null;
}

module.exports = {
	SCOPE_ORDER,
	MISSING_VALUE,
	scopeRank,
	addressToEditorPath,
	editorPathToAddress,
	editorPathToLeafPath,
	isLeafValue,
	enumerateLeafPaths,
	valueAtLeafPath,
	leafValuesEqual,
	buildProvenanceMapFromRows,
	computeProvenanceByDiff,
	lookupProvenance,
};
